import React, { useCallback, useEffect, useState } from 'react';
import { Briefcase, Loader2, Database } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import BDOutlookPersonalCard from './BDOutlookPersonalCard';
import BDMeetingSkipRules from './BDMeetingSkipRules';
import BDStyleProfileCard from './BDStyleProfileCard';

/**
 * BD-01 — a person's own BD Assistant settings.
 *
 * Loads GET /api/bd-assistant/me, saves each change with PATCH /me (the server
 * returns the whole settings object, which replaces local state — no optimistic
 * update). The master switch is read-only here: turning the assistant ON is an
 * admin rollout action; this page lets a person tune steps and options.
 *
 * Step toggles are driven by one config array. Steps that depend on Outlook
 * render now but are disabled with a note, so when the Outlook half lands only
 * the `outlookReady` flag flips — no new UI scaffolding.
 */

interface Settings {
  enabled: boolean;
  steps: Record<string, boolean>;
  followup_reminder_days: number;
  capture_delay_minutes?: number;
  send_mode: string;
  skip_title_keywords: string[];
  internal_domains: string[];
  default_cc: string[];
  zoho_initials: string | null;
  banned_phrases?: string[];
  firm_banned_phrases?: string[];
  firm_default_cc?: string[];
  firm_tone_notes?: string | null;
  tone_notes?: string | null;
  coordinator?: { name: string | null; email: string } | null;
  signature?: string | null;
}

// `requires` says which capability a step needs. Outlook steps need the person's
// own Outlook to be connected (per-user); until then they render but are disabled.
// `optIn`: a step that is OFF until the person turns it on (backend DEFAULT_OFF_STEPS);
// the note says so, and what turning it on means.
const STEP_CONFIG: { key: string; label: string; help: string; requires: 'zoho' | 'outlook' | null; optIn?: { off: string; on: string } }[] = [
  { key: 'prep_briefs', label: 'Prep briefs', help: 'A brief in Slack the morning of a meeting', requires: 'outlook' },
  { key: 'transcript_asks', label: 'Transcript asks', help: 'Ask for a transcript or voice debrief after a call', requires: 'outlook' },
  { key: 'zoho_updates', label: 'Zoho updates', help: 'Propose CRM notes and deal changes for approval', requires: 'zoho' },
  { key: 'followup_drafts', label: 'Follow-up drafts', help: 'Draft a follow-up email in your voice', requires: 'outlook' },
  {
    key: 'coaching',
    label: 'Coaching',
    help: 'Score your logged calls privately and get one thing to try next time',
    requires: null,
    optIn: {
      off: 'Off until you turn it on. When on, every call you log with a transcript is scored. Only you can see the scores; you can share one with a named reviewer.',
      on: 'On: your logged transcripts are scored, privately. The one thing to try next time goes into your next prep brief.',
    },
  },
  {
    key: 'coach_trends',
    label: 'Share my coaching trend',
    help: 'Count your scores, anonymously, toward a firm-wide trend (needs Coaching on)',
    requires: null,
    optIn: {
      off: 'Off. Nothing of yours is in the firm view. When on, only your monthly averages count, with no names, calls or quotes, and the firm view shows only when enough people share.',
      on: 'On: your monthly averages count toward the anonymous firm trend. Turn it off at any time and they drop out.',
    },
  },
  { key: 'radar', label: 'Radar', help: 'Up to five people who need attention this week, why now, and a Slack message on Monday morning', requires: null },
  {
    key: 'thread_watch',
    label: 'Thread watcher',
    help: 'Draft a reply when a BD contact is waiting on you, and remind you when a thread is waiting on them',
    requires: 'outlook',
    optIn: {
      off: 'Off until you turn it on. When on, the assistant checks your own mailbox every 15 minutes, only threads with your BD contacts and accounts. It never moves, deletes, marks as read or sends anything; replies wait in Approvals.',
      on: 'On: checking only threads with your BD contacts and accounts, every 15 minutes. Nothing in your mailbox is changed.',
    },
  },
  { key: 'pipeline_review', label: 'Pipeline review', help: 'Your deals, questions and changes to approve in Slack before the pipeline meeting', requires: 'zoho' },
  {
    key: 'style_profile',
    label: 'Learn from my edits',
    help: 'Follow-up drafts pick up the changes you keep making to them',
    // Learning reads only the stored diffs of your own edits, not Outlook.
    requires: null,
    optIn: {
      off: 'Off until you turn it on. When on, the assistant notices short phrases you keep cutting, adding or swapping in follow-up drafts, and writes new drafts that way. Only what it learned is kept, never your emails. Turning it off forgets it.',
      on: 'On: learning from your edits to follow-up drafts. What it has learned is listed under Follow-up emails.',
    },
  },
];

const INPUT_CLS = 'w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50';

/** Comma-separated list, saved on blur. */
const ListField: React.FC<{
  label: string; help: string; testid: string; value: string[];
  onSave: (list: string[]) => void; disabled?: boolean;
}> = ({ label, help, testid, value, onSave, disabled }) => {
  const [draft, setDraft] = useState(value.join(', '));
  useEffect(() => { setDraft(value.join(', ')); }, [value]);
  const commit = () => {
    const list = draft.split(',').map((s) => s.trim()).filter(Boolean);
    if (list.join('\u0001') !== value.join('\u0001')) onSave(list);
  };
  return (
    <div>
      <label className="block text-sm font-medium text-gray-900">{label}</label>
      <p className="text-xs text-gray-500 mb-1">{help}</p>
      <input
        type="text" value={draft} disabled={disabled} data-testid={testid}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        className={INPUT_CLS}
      />
    </div>
  );
};

const TextField: React.FC<{
  label: string; help: string; testid: string; value: string;
  onSave: (v: string) => void; disabled?: boolean; maxLength?: number;
}> = ({ label, help, testid, value, onSave, disabled, maxLength }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-900">{label}</label>
      <p className="text-xs text-gray-500 mb-1">{help}</p>
      <input
        type="text" value={draft} disabled={disabled} maxLength={maxLength} data-testid={testid}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { if (draft.trim() !== value) onSave(draft.trim()); }}
        className={INPUT_CLS}
      />
    </div>
  );
};

/** Multi-line text, saved on blur. */
const TextAreaField: React.FC<{
  label: string; help: string; testid: string; value: string;
  onSave: (v: string) => void; disabled?: boolean; maxLength?: number; rows?: number; placeholder?: string;
}> = ({ label, help, testid, value, onSave, disabled, maxLength, rows = 3, placeholder }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-900">{label}</label>
      <p className="text-xs text-gray-500 mb-1">{help}</p>
      <textarea
        value={draft} disabled={disabled} maxLength={maxLength} rows={rows} placeholder={placeholder} data-testid={testid}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { if (draft.trim() !== value) onSave(draft.trim()); }}
        className={INPUT_CLS}
      />
    </div>
  );
};

/** BD-10: the person copied on follow-ups with a handoff line. Saved when either field loses focus. */
const CoordinatorField: React.FC<{
  value: { name: string | null; email: string } | null;
  onSave: (v: { name: string; email: string } | null) => void; disabled?: boolean;
}> = ({ value, onSave, disabled }) => {
  const [name, setName] = useState(value?.name || '');
  const [email, setEmail] = useState(value?.email || '');
  useEffect(() => { setName(value?.name || ''); setEmail(value?.email || ''); }, [value]);
  const commit = () => {
    const next = email.trim() ? { name: name.trim(), email: email.trim() } : null;
    const same = (next?.email || '') === (value?.email || '') && (next?.name || '') === (value?.name || '');
    if (!same) onSave(next);
  };
  return (
    <div>
      <label className="block text-sm font-medium text-gray-900">Coordinator</label>
      <p className="text-xs text-gray-500 mb-1">Copied on follow-ups, with a line handing the next steps to them. Leave the email empty for none.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input type="text" placeholder="Name" value={name} disabled={disabled} data-testid="bd-coordinator-name"
          onChange={(e) => setName(e.target.value)} onBlur={commit} className={INPUT_CLS} />
        <input type="email" placeholder="Email" value={email} disabled={disabled} data-testid="bd-coordinator-email"
          onChange={(e) => setEmail(e.target.value)} onBlur={commit} className={INPUT_CLS} />
      </div>
    </div>
  );
};

const NumberField: React.FC<{
  label: string; help: string; testid: string; value: number;
  onSave: (n: number) => void; disabled?: boolean; min?: number; max?: number;
}> = ({ label, help, testid, value, onSave, disabled, min, max }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-900">{label}</label>
      <p className="text-xs text-gray-500 mb-1">{help}</p>
      <input
        type="number" value={draft} disabled={disabled} min={min} max={max} data-testid={testid}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const n = Number(draft);
          if (Number.isInteger(n) && n !== value && (min === undefined || n >= min) && (max === undefined || n <= max)) onSave(n);
        }}
        className={INPUT_CLS}
      />
    </div>
  );
};

const Toggle: React.FC<{ checked: boolean; onChange: (next: boolean) => void; disabled?: boolean; label: string }> = ({
  checked, onChange, disabled, label,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    data-testid={`bd-toggle-${label}`}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
      checked ? 'bg-blue-600' : 'bg-gray-200'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const BDAssistantSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [zohoReady, setZohoReady] = useState(true);
  const [zohoFirmConnected, setZohoFirmConnected] = useState(true);
  const [quietHours, setQuietHours] = useState<{ start: string; end: string; timezone: string | null } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [{ data }, caps] = await Promise.all([
        api.get('/api/bd-assistant/me'),
        api.get('/api/bd-assistant/me/capabilities').then((r) => r.data?.capabilities).catch(() => null),
      ]);
      setSettings(data.settings);
      if (caps) {
        setZohoReady(Boolean(caps.zoho?.ready));
        setZohoFirmConnected(Boolean(caps.zoho?.firm_connected));
        setQuietHours(caps.timing?.quiet_hours || null);
      }
      // Outlook connection status is reported by BDOutlookPersonalCard via
      // onStatusChange, so the step gating below reflects it live.
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSteps = useCallback(async (nextSteps: Record<string, boolean>) => {
    setSaving(true);
    try {
      const { data } = await api.patch('/api/bd-assistant/me', { steps: nextSteps });
      setSettings(data.settings);
    } catch {
      toast.error('Could not save — try again.');
    } finally {
      setSaving(false);
    }
  }, []);

  // Save any of the non-step preference fields (skip list, CC, initials, etc.).
  const savePrefs = useCallback(async (patch: Record<string, unknown>) => {
    setSaving(true);
    try {
      const { data } = await api.patch('/api/bd-assistant/me', patch);
      setSettings(data.settings);
    } catch {
      toast.error('Could not save — try again.');
    } finally {
      setSaving(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-8" data-testid="bd-settings-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your BD Assistant settings…
      </div>
    );
  }
  if (loadError || !settings) {
    return (
      <div className="py-8 text-sm text-gray-600" data-testid="bd-settings-error">
        Could not load your BD Assistant settings.{' '}
        <button type="button" className="text-blue-600 underline" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bd-assistant-settings">
      <div className="flex items-start gap-3">
        <Briefcase className="w-5 h-5 text-gray-700 mt-0.5" />
        <div>
          <h3 className="text-sm font-semibold text-gray-900">BD Assistant</h3>
          <p className="text-sm text-gray-500">
            {settings.enabled
              ? 'Your BD Assistant is switched on. Choose which steps run for you below.'
              : 'An admin has not switched the BD Assistant on for you yet. You can still set your preferences; they take effect once it is on.'}
          </p>
        </div>
      </div>

      {/* Zoho status card — mirrors the Outlook card's layout. Zoho is a firm
          (tenant) connection, so there is no per-user connect button here; the
          card reports whether the firm is connected and whether this person is
          mapped, and names a "reconnect" path (no silent failure). */}
      <div className="rounded-xl border border-gray-200 bg-white p-5" data-testid="bd-zoho-status-card">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${zohoReady ? 'bg-emerald-50' : 'bg-amber-50'}`}>
            <Database className={`w-5 h-5 ${zohoReady ? 'text-emerald-600' : 'text-amber-600'}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Zoho CRM</h3>
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  zohoReady ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                }`}
                data-testid="bd-zoho-status-badge"
              >
                {zohoReady ? 'Ready' : zohoFirmConnected ? 'Not mapped' : 'Not connected'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5 max-w-md">
              {zohoReady
                ? 'Connected. CRM notes from the assistant are authored under your name.'
                : zohoFirmConnected
                  ? 'Your firm is connected, but your Zoho user is not mapped yet. Until an admin syncs Zoho users, notes post under the firm account. This is a firm connection — there is nothing for you to connect here.'
                  : 'Zoho CRM is not connected for your firm, so the “Zoho updates” step cannot run. An admin can reconnect it on the Integrations page.'}
            </p>
          </div>
        </div>
      </div>

      {/* Per-user Outlook connect/disconnect — here in the BD settings, which
          every Stemmler user can reach (unlike the admin-only Integrations page).
          Connecting affects only this person. The card manages its own state and
          calls onChange so the step gating below reflects it live. */}
      <BDOutlookPersonalCard onStatusChange={(c) => setOutlookConnected(c)} />

      <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
        {STEP_CONFIG.map((step) => {
          const needsOutlook = step.requires === 'outlook' && !outlookConnected;
          const needsZoho = step.requires === 'zoho' && !zohoReady;
          const blocked = needsOutlook || needsZoho;
          const checked = settings.steps[step.key] !== false;
          return (
            <div key={step.key} className="flex items-center justify-between px-4 py-3">
              <div className="pr-4">
                <div className="text-sm font-medium text-gray-900">{step.label}</div>
                <div className="text-xs text-gray-500">{step.help}</div>
                {step.optIn && (
                  <div className={`text-xs mt-0.5 ${checked && !blocked ? 'text-emerald-700' : 'text-blue-700'}`} data-testid={`bd-step-optin-${step.key}`}>
                    {checked && !blocked ? step.optIn.on : step.optIn.off}
                  </div>
                )}
                {needsOutlook && (
                  <div className="text-xs text-amber-600 mt-0.5">Connect your Outlook below to use this.</div>
                )}
                {needsZoho && (
                  <div className="text-xs text-amber-600 mt-0.5">
                    {zohoFirmConnected
                      ? 'Your Zoho user is not mapped yet — an admin can sync Zoho users. Until then notes post under the firm account.'
                      : 'Zoho is not connected for your firm — an admin can reconnect it on the Integrations page.'}
                  </div>
                )}
              </div>
              <Toggle
                label={step.label}
                checked={checked && !blocked}
                disabled={blocked || saving || !settings.enabled}
                onChange={(next) => saveSteps({ ...settings.steps, [step.key]: next })}
              />
            </div>
          );
        })}
      </div>

      {/* Preferences — the rest of the per-person settings from the ticket.
          List fields are comma-separated and save on blur. */}
      <div className="space-y-4">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Preferences</h4>

        {/* Working & quiet hours are reused from the Slack notification
            preferences (one source of truth) — shown here, edited there. */}
        <div className="rounded-lg border border-gray-200 px-4 py-3" data-testid="bd-quiet-hours">
          <div className="text-sm font-medium text-gray-900">Working &amp; quiet hours</div>
          <div className="text-xs text-gray-500 mt-0.5">
            {quietHours
              ? `Quiet ${quietHours.start}–${quietHours.end}${quietHours.timezone ? ` (${quietHours.timezone})` : ''}. The assistant will not message you then; outside these is your working time.`
              : 'No quiet hours set — the assistant can message you at any time.'}{' '}
            Shared with your Slack notifications.{' '}
            <a href="/app/settings?tab=notifications" className="text-blue-600 underline" data-testid="bd-quiet-hours-edit">
              Edit in Notifications
            </a>
          </div>
        </div>

        <ListField
          label="Skip meetings with these words in the title"
          help="Comma-separated. e.g. Huddle, Principal Connect"
          testid="bd-skip-keywords"
          value={settings.skip_title_keywords}
          onSave={(list) => savePrefs({ skip_title_keywords: list })}
          disabled={saving}
        />
        {/* BD-05: learned / approved meeting skip rules, each undoable. */}
        <BDMeetingSkipRules />
        <ListField
          label="Internal email domains"
          help="Treated as internal (not a client). Comma-separated. e.g. thestemmlergroup.com"
          testid="bd-internal-domains"
          value={settings.internal_domains}
          onSave={(list) => savePrefs({ internal_domains: list })}
          disabled={saving}
        />
        <ListField
          label="Default CC on follow-up drafts"
          help={`Comma-separated email addresses, e.g. a partner or coordinator${settings.firm_default_cc?.length ? `. Your firm also copies: ${settings.firm_default_cc.join(', ')}` : ''}`}
          testid="bd-default-cc"
          value={settings.default_cc}
          onSave={(list) => savePrefs({ default_cc: list })}
          disabled={saving}
        />

        {/* BD-10: house rules for follow-up drafts. */}
        <div className="space-y-4 rounded-lg border border-gray-200 px-4 py-3" data-testid="bd-followup-rules">
          <div className="text-sm font-medium text-gray-900">Follow-up emails</div>
          <TextAreaField
            label="How you write"
            help={`Tone notes the drafts follow, e.g. Short and warm. No exclamation marks.${settings.firm_tone_notes ? ` Your firm's house style also applies: ${settings.firm_tone_notes}` : ''}`}
            testid="bd-tone-notes"
            value={settings.tone_notes || ''}
            onSave={(v) => savePrefs({ tone_notes: v })}
            disabled={saving}
            maxLength={1000}
          />
          {/* BD-10: what "Learn from my edits" has picked up, while it is on. */}
          <BDStyleProfileCard on={settings.enabled && settings.steps.style_profile === true} />
          <ListField
            label="Phrases you never use"
            help={`Comma-separated, on top of the firm's list${settings.firm_banned_phrases?.length ? `: ${settings.firm_banned_phrases.map((p) => `"${p}"`).join(', ')}` : ''}.`}
            testid="bd-banned-phrases"
            value={settings.banned_phrases || []}
            onSave={(list) => savePrefs({ banned_phrases: list })}
            disabled={saving}
          />
          <CoordinatorField
            value={settings.coordinator || null}
            onSave={(v) => savePrefs({ coordinator: v })}
            disabled={saving}
          />
          <TextAreaField
            label="Sign-off"
            help="Ends every follow-up. Leave empty for Best, and your first name."
            testid="bd-signature"
            value={settings.signature || ''}
            onSave={(v) => savePrefs({ signature: v })}
            disabled={saving}
            maxLength={500}
            placeholder={'Best,\nYour first name'}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            label="Your initials for Zoho Next Steps"
            help="Used when the assistant logs a next step"
            testid="bd-zoho-initials"
            value={settings.zoho_initials || ''}
            onSave={(v) => savePrefs({ zoho_initials: v })}
            disabled={saving}
            maxLength={16}
          />
          <NumberField
            label="Follow-up reminder (days)"
            help="How long after a meeting to remind you"
            testid="bd-reminder-days"
            value={settings.followup_reminder_days}
            onSave={(n) => savePrefs({ followup_reminder_days: n })}
            disabled={saving}
            min={1}
            max={90}
          />
          <NumberField
            label="Ask about a call after (minutes)"
            help="How long after a meeting ends to ask for notes"
            testid="bd-capture-delay"
            value={settings.capture_delay_minutes ?? 15}
            onSave={(n) => savePrefs({ capture_delay_minutes: n })}
            disabled={saving}
            min={0}
            max={240}
          />
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Email is drafts-only — every email is drafted and never sent without your approval.
      </p>
    </div>
  );
};

export default BDAssistantSettings;
